import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormField, Input } from '@/shared/ui';

import { OFFLINE_ADDRESS_MAX, type OfflineAddressDraft } from '../types';

export interface OfflineDeliveryAddressFormProps {
  value: OfflineAddressDraft;
  onChange: (next: OfflineAddressDraft) => void;
  disabled?: boolean;
}

export function OfflineDeliveryAddressForm({
  value,
  onChange,
  disabled = false,
}: OfflineDeliveryAddressFormProps) {
  const { t } = useTranslation('offlineOrders');
  const [showCoords, setShowCoords] = useState(
    () => value.latitude.trim() !== '' || value.longitude.trim() !== '',
  );

  const patch = (field: keyof OfflineAddressDraft, next: string) => {
    onChange({ ...value, [field]: next });
  };

  return (
    <section className="flex flex-col gap-3 border-t border-border pt-4">
      <div>
        <h2 className="text-sm font-semibold text-fg">{t('fulfillment.addressTitle')}</h2>
        <p className="mt-1 text-xs text-fg-muted">{t('fulfillment.addressOptional')}</p>
      </div>

      <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-muted/60 p-3">
        <FormField>
          <FormField.Label>{t('fulfillment.line1')}</FormField.Label>
          <FormField.Control>
            <Input
              autoComplete="address-line1"
              maxLength={OFFLINE_ADDRESS_MAX.line1}
              placeholder={t('fulfillment.line1Placeholder')}
              disabled={disabled}
              value={value.line1}
              onChange={(event) => patch('line1', event.target.value)}
            />
          </FormField.Control>
        </FormField>

        <FormField>
          <FormField.Label>{t('fulfillment.line2')}</FormField.Label>
          <FormField.Control>
            <Input
              autoComplete="address-line2"
              maxLength={OFFLINE_ADDRESS_MAX.line2}
              placeholder={t('fulfillment.line2Placeholder')}
              disabled={disabled}
              value={value.line2}
              onChange={(event) => patch('line2', event.target.value)}
            />
          </FormField.Control>
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField>
            <FormField.Label>{t('fulfillment.city')}</FormField.Label>
            <FormField.Control>
              <Input
                autoComplete="address-level2"
                maxLength={OFFLINE_ADDRESS_MAX.city}
                placeholder={t('fulfillment.cityPlaceholder')}
                disabled={disabled}
                value={value.city}
                onChange={(event) => patch('city', event.target.value)}
              />
            </FormField.Control>
          </FormField>
          <FormField>
            <FormField.Label>{t('fulfillment.state')}</FormField.Label>
            <FormField.Control>
              <Input
                autoComplete="address-level1"
                maxLength={OFFLINE_ADDRESS_MAX.state}
                placeholder={t('fulfillment.statePlaceholder')}
                disabled={disabled}
                value={value.state}
                onChange={(event) => patch('state', event.target.value)}
              />
            </FormField.Control>
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField>
            <FormField.Label>{t('fulfillment.pincode')}</FormField.Label>
            <FormField.Control>
              <Input
                autoComplete="postal-code"
                inputMode="numeric"
                maxLength={OFFLINE_ADDRESS_MAX.pincode}
                placeholder={t('fulfillment.pincodePlaceholder')}
                disabled={disabled}
                value={value.pincode}
                onChange={(event) => patch('pincode', event.target.value)}
              />
            </FormField.Control>
          </FormField>
          <FormField>
            <FormField.Label>{t('fulfillment.label')}</FormField.Label>
            <FormField.Control>
              <Input
                autoComplete="off"
                maxLength={OFFLINE_ADDRESS_MAX.label}
                placeholder={t('fulfillment.labelPlaceholder')}
                disabled={disabled}
                value={value.label}
                onChange={(event) => patch('label', event.target.value)}
              />
            </FormField.Control>
          </FormField>
        </div>

        {showCoords ? (
          <div className="grid grid-cols-2 gap-3">
            <FormField>
              <FormField.Label>{t('fulfillment.latitude')}</FormField.Label>
              <FormField.Control>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  disabled={disabled}
                  value={value.latitude}
                  onChange={(event) => patch('latitude', event.target.value)}
                />
              </FormField.Control>
            </FormField>
            <FormField>
              <FormField.Label>{t('fulfillment.longitude')}</FormField.Label>
              <FormField.Control>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  disabled={disabled}
                  value={value.longitude}
                  onChange={(event) => patch('longitude', event.target.value)}
                />
              </FormField.Control>
            </FormField>
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="self-start"
            disabled={disabled}
            onClick={() => setShowCoords(true)}
          >
            {t('fulfillment.addCoords')}
          </Button>
        )}
      </div>
    </section>
  );
}
